import configureMockStore from 'redux-mock-store';
import thunk from 'redux-thunk';

jest.mock('../../global/axios', () => ({
    get: jest.fn(),
    post: jest.fn(),
    put: jest.fn(),
    delete: jest.fn(),
}));

// `billboard_actions` builds its S3 payload with the `form-data` package
// (not the browser FormData), which has no readable getter. Replace it
// with a recorder so the test can assert on what was appended and in
// what order.
jest.mock('form-data', () => {
    class RecordingFormData {
        constructor() {
            this.entries = [];
        }
        append(name, value) {
            this.entries.push([name, value]);
        }
        names() {
            return this.entries.map(([name]) => name);
        }
        get(name) {
            const found = this.entries.find(([key]) => key === name);
            return found ? found[1] : null;
        }
    }
    return RecordingFormData;
});

import axios from '../../global/axios';
import { uploadMedia } from './billboard_actions';

const mockStore = configureMockStore([thunk]);

// The signed S3 POST policy carries a `Cache-Control` condition. An S3
// POST policy condition REQUIRES a matching form field — if the client
// omits it, S3 rejects the upload outright. These tests pin that the
// value the server hands back is the value we send, and that it goes in
// before the `file` field (S3 ignores anything after `file`).

const SIGNED_RESPONSE = {
    data: {
        key: 'billboard_uploads/uuid.png',
        AWSAccessKeyId: 'AKIA',
        policy: 'cG9saWN5',
        signature: 'c2ln',
        content_type: 'image/png',
        cache_control: 'public, max-age=31536000, immutable',
    },
};

const S3_XML =
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<PostResponse><Location>https://worshipkit.s3.amazonaws.com/billboard_uploads/uuid.png</Location></PostResponse>';

function s3FormData() {
    const s3Call = axios.post.mock.calls.find(
        ([url]) => url === 'https://worshipkit.s3.amazonaws.com'
    );
    return s3Call ? s3Call[1] : null;
}

describe('uploadMedia — S3 Cache-Control form field', () => {
    beforeEach(() => {
        axios.get.mockReset();
        axios.post.mockReset();
        global.alert = jest.fn();
        // jsdom in this Jest version has no `crypto.randomUUID`; the
        // action calls it to build the (now-discarded) local filename.
        if (typeof global.crypto === 'undefined') {
            global.crypto = { randomUUID: () => 'local-uuid' };
        } else if (typeof global.crypto.randomUUID !== 'function') {
            global.crypto.randomUUID = () => 'local-uuid';
        }
    });

    async function runUpload(signedResponse = SIGNED_RESPONSE) {
        axios.post.mockImplementation((url) => {
            if (url === '/billboard/signed_urls/v2') {
                return Promise.resolve(signedResponse);
            }
            if (url === 'https://worshipkit.s3.amazonaws.com') {
                return Promise.resolve({ data: S3_XML });
            }
            return Promise.resolve({ data: {} });
        });
        axios.get.mockResolvedValue({ data: [] });

        const store = mockStore({});
        const file = { name: 'photo.png', type: 'image/png' };
        store.dispatch(uploadMedia(file, null));
        // The thunk doesn't return its promise chain, so drain the
        // microtask queue until the S3 POST has been issued.
        for (let i = 0; i < 10 && s3FormData() === null; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await Promise.resolve();
        }
    }

    it('sends the Cache-Control value the server returned', async () => {
        await runUpload();
        const form = s3FormData();
        expect(form).not.toBeNull();
        expect(form.get('Cache-Control')).toBe('public, max-age=31536000, immutable');
    });

    it('sends Cache-Control before the file field', async () => {
        // S3 ignores any form field that comes after `file`, so an
        // ordering regression is a silent policy-mismatch rejection.
        await runUpload();
        const names = s3FormData().names();
        expect(names).toContain('Cache-Control');
        expect(names.indexOf('Cache-Control')).toBeLessThan(names.indexOf('file'));
    });

    it('omits the field entirely when the server does not send one', async () => {
        // Belt and braces for a rolling deploy where the JS bundle is
        // newer than the server: sending an empty Cache-Control would
        // fail the policy condition, sending nothing matches an
        // unchanged policy.
        const legacy = { data: { ...SIGNED_RESPONSE.data } };
        delete legacy.data.cache_control;
        await runUpload(legacy);
        expect(s3FormData().names()).not.toContain('Cache-Control');
    });

    it('still uses the server-returned key rather than a locally generated one', async () => {
        await runUpload();
        expect(s3FormData().get('key')).toBe('billboard_uploads/uuid.png');
    });
});
